import React, { useContext, useEffect, useRef, useState } from 'react'

import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  Position
} from 'geojson'
import { GeoJSONSource, MapboxGeoJSONFeature } from 'mapbox-gl'
import Map, { Layer, MapRef, ScaleControl, Source } from 'react-map-gl'

import { API, getApiData } from '../api'
import { AppContext } from '../context'
import { clusterCountLayerStyle } from '../map-layers-and-sources/cluster-count'
import { clustersLayerStyle } from '../map-layers-and-sources/clusters'
import {
  booksEndpoint,
  BuildGeoJSONProps,
  FeatureCallback,
  FeatureLayer,
  initialLatitudePosition,
  initialLongitudePosition,
  SelectionType
} from '../map-layers-and-sources/common'
import {
  placesActiveLayerStyle,
  placesActiveSource
} from '../map-layers-and-sources/places-active'
import { useChartSets } from '../map-layers-and-sources/useChartsHook'
import {
  placesSource,
  usePlaceLayerStyle
} from '../map-layers-and-sources/usePlacesHook'
import { useSocialLayerStyle } from '../map-layers-and-sources/useSocialHook'
import {
  useUglClusterCountLayerStyle,
  useUglClusterLayerStyle,
  useUserGeneratedLocationsLayerStyle
} from '../map-layers-and-sources/useUserGeneratedLocationsHook'
import {
  AttributionControl,
  BookmarksControl,
  FilterControl,
  GeocoderControl,
  GroupControlContainer,
  InfoControl,
  NavigationControl,
  ProfileControl,
  SatelliteViewControl,
  TrialControl,
  ZoomDisplayControl
} from './MapControls/'
import LocationPopup from './Popovers/LocationPopup'

import { Spinner } from 'react-bootstrap'
import type { IMapPin } from '../typings/common'
import { fixValues, removeDuplicates } from '../utils/mapHelpers'
import CustomSearchBox from './CustomSearchBox/CustomSearchBox'

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN as string

const MapView: React.FC = () => {
  const booksRouteURL = process.env.REACT_APP_BOOKS_ROUTE_URL

  // Get query parameters from the URL
  const urlSearchParams = new URLSearchParams(window.location.search)
  const params = Object.fromEntries(urlSearchParams.entries())
  const { lng, lat, zoom } = params

  const [viewState, setViewState] = useState(() => {
    return {
      longitude: lng ? parseFloat(lng) : initialLongitudePosition,
      latitude: lat ? parseFloat(lat) : initialLatitudePosition,
      zoom: zoom ? parseFloat(zoom) : 3,
      // maxBounds: [
      //   [-220, -85], // Southwest coordinates
      //   [220, 85]  // Northeast coordinates
      // ] as LngLatBoundsLike,
      minZoom: 0,
      maxZoom: 16,
      attributionControl: false
    }
  })

  useEffect(() => {
    const update: {
      [key: string]: number | undefined
    } = {}
    if (lng) update['longitude'] = parseFloat(lng)
    if (lat) update['latitude'] = parseFloat(lat)
    if (zoom) update['zoom'] = parseFloat(zoom)
    setViewState({ ...viewState, ...update })

    if (
      lng !== null &&
      lng !== undefined &&
      lng !== '' &&
      lat !== null &&
      lat !== undefined &&
      lat !== ''
    ) {
      placesSource.data = locations
    }
  }, [lng, lat, zoom])

  const [locationId, setLocationId] = useState(params.locationId || null)
  const [selectedPin, setSelectedPin] = useState<SelectionType | null>(null)
  const [isMapLoaded, setIsMapLoaded] = useState(false)

  const [newPostPopupData, setNewPostPopupData] = useState({
    show: false,
    lat: 0,
    lng: 0
  })
  const [locationPopupData, setLocationPopupData] = useState({
    show: false,
    lat: 0,
    lng: 0,
    features: [] as GeoJsonProperties[],
    matchingName: ''
  })

  const [isSatelliteView, setIsSatelliteView] = useState(false)
  const [mapStyle, setMapStyle] = useState(
    'mapbox://styles/imrayonline/ckg7w26bj5z1019olz49dhfuq'
  )

  const [isCommunityContentVisible, setIsCommunityContentVisible] =
    useState(false)

  const [mousePosition, setMousePosition] = useState({
    lat: viewState.latitude,
    lng: viewState.longitude
  })

  const flyToLocationRef = useRef<IMapPin | null>(null)

  const { placesLayerStyle, togglePlacesLayerOpacity } = usePlaceLayerStyle()
  const {
    userGeneratedLocationsLayerStyle,
    toggleUserGeneratedLocationsLayerOpacity,
    setLayerVisibility: uglSetLayerVisibility,
    toggleUserGeneratedLocationsLayerVisibility: uglToggleLayerVisibility
  } = useUserGeneratedLocationsLayerStyle()
  const {
    layerStyle: uglClusterCountLayerStyle,
    layerVisibility: uglClusterCountLayerVisibility,
    setLayerVisibility: uglClusterCountSetLayerVisibility,
    toggleLayerVisibility: uglClusterCountToggleLayerVisibility
  } = useUglClusterCountLayerStyle()
  const {
    layerStyle: uglClusterLayerStyle,
    layerVisibility: uglClusterntLayerVisibility,
    setLayerVisibility: uglClusterSetLayerVisibility,
    toggleLayerVisibility: uglClusterToggleLayerVisibility
  } = useUglClusterLayerStyle()
  const {
    layerStyle: socialLayerStyle,
    toggleOpacity: toggleSocialLayerOpacity,
    layerVisibility: socialLayerVisibility,
    setLayerVisibility: socialSetLayerVisibility,
    toggleLayerVisibility: socialToggleLayerVisibility
  } = useSocialLayerStyle()
  const {
    sources: chartSetsSources,
    layers: chartSetsLayers,
    charts: chartSets,
    layerVisibility: chartSetsLayerVisibility,
    geojsonSources: chartSetsGeojsonSources,
    geojsonLayers: chartSetsGeojsonLayers,
    setLayerVisibility: setChartSetsLayerVisibility
  } = useChartSets()

  // GeoJSONSourceRaw
  const [activePlaces, setActivePlaces] = useState({
    type: 'FeatureCollection' as const,
    features: [] as any[]
  })

  const {
    chartsView,
    setChartsView,
    onTogglePopover,
    placesSourceQuery,
    userGeneratedLocationsSourceQuery,
    setDetailsOverlayData,
    userSubscriptionStatus,
    setTrialDataViewCounter,
    resetHighlightedPin,
    setResetHighlightedPin,
    locationToView,
    setLocationToView,
    isProfileCompleted,
    userHasDismissedCompleteProfilePopup,
    activePopover,
    shouldResetSocialPins,
    setShouldResetSocialPins,
    locations
  } = useContext(AppContext)

  const isSubscribed = userSubscriptionStatus?.hasSubscription

  const { chartsAreLoaded, chartsAreVisible } = chartsView
  const showZoomLevel = true

  useEffect(() => {
    setCommunityContentVisibility(false)
  }, [])

  useEffect(() => {
    if (resetHighlightedPin) {
      setSelectedPin(null)

      highlightPin(undefined, undefined)
      setResetHighlightedPin(false)
    }
  }, [resetHighlightedPin])

  useEffect(() => {
    if (locationToView) {
      flyToLocationRef.current = locationToView
      if (
        locationToView.layer === 'places' ||
        locationToView.layer === 'user-generated-locations'
      ) {
        checkIfSubscribed(locationToView.id, queryDetails)
      } else {
        setLocationToView(null)
      }

      /*     else if (locationToView.layer === 'social') {
        flyToSocialPost(locationToView.id).then()
      }*/
    }
  }, [locationToView])

  useEffect(() => {
    if (shouldResetSocialPins) {
      /*reloadSocialPins()*/
      setShouldResetSocialPins(false)
    }
  }, [shouldResetSocialPins])

  const flyToSocialPost = async (locationId: string) => {
    const socialURL = process.env.REACT_APP_SOCIAL_URL
    const url = `${socialURL}api/socialPost/getSocialPostById/${locationId}`
    const data = await getApiData(url)

    mapRef.current?.flyTo({
      center: [data.longitude, data.latitude]
    })
    flyToLocationRef.current = null
  }

  const toggleSatelliteView = () => {
    const newValue = !isSatelliteView
    setIsSatelliteView(newValue)

    placesSource.data = `${booksRouteURL}${booksEndpoint}`

    if (newValue) {
      // Satellite
      setMapStyle('mapbox://styles/imrayonline/cl8vqkxyl003314ryuzoqb2wd')
    } else {
      // Normal
      setMapStyle('mapbox://styles/imrayonline/ckg7w26bj5z1019olz49dhfuq')
    }

    if (chartsAreVisible) {
      setChartsView({ ...chartsView, chartsAreLoaded: false })
      // We need to load the charts again since the style of the layer changed
      loadChartSets(true)
    }
  }

  const setChartLayerVisibility = (on: boolean) => {
    setChartSetsLayerVisibility(on)
    setChartsView({ ...chartsView, chartsAreVisible: on })
  }

  function loadChartSets(isChartsEnabled: boolean) {
    if (isChartsEnabled && !chartsAreLoaded) {
      setChartsView({ chartsAreLoaded: true, chartsAreVisible: true })
    } else if (isChartsEnabled && chartsAreLoaded) {
      setChartLayerVisibility(true)
    } else {
      setChartLayerVisibility(false)
    }
  }

  const toggleChartsInfoOverlay = (on: boolean) => {
    // DEVNOTE: We're not using a free trial for raster charts for now, but may reinstate it later
    if (!isSubscribed) {
      onTogglePopover('raster')
    } else {
      loadChartSets(on)
    }

    return on
  }

  const setCommunityContentVisibility = (newValue: boolean) => {
    uglSetLayerVisibility(newValue)
    uglToggleLayerVisibility(newValue)
    uglClusterCountSetLayerVisibility(newValue)
    uglClusterCountToggleLayerVisibility(newValue)
    uglClusterSetLayerVisibility(newValue)
    uglClusterToggleLayerVisibility(newValue)
    socialSetLayerVisibility(newValue)
    socialToggleLayerVisibility(newValue)
  }

  const toggleCommunityContent = () => {
    const newValue = !isCommunityContentVisible
    setIsCommunityContentVisible(newValue)
    setCommunityContentVisibility(newValue)
  }

  const mapRef = useRef<MapRef>(null)

  const setActive = (geoJson: null | Feature<Geometry, GeoJsonProperties>) => {
    setActivePlaces(state => ({
      ...state,
      features: geoJson ? [geoJson] : []
    }))
  }

  // output MapboxGeoJSONFeature
  const buildGeoJson = (
    { id, name, iconType, position }: BuildGeoJSONProps,
    type = 'Feature' as const
  ): Feature => {
    const newPosition =
      typeof position === 'object' ? position : JSON.parse(position)

    return {
      type,
      properties: { id, name, iconType, position },
      geometry: {
        type: 'Point',
        coordinates: [newPosition.longitude, newPosition.latitude]
      }
    }
  }

  const setSelectedState = function (
    selection: SelectionType | null,
    active: boolean
  ) {
    if (!selection) return

    mapRef.current?.setFeatureState(
      { source: selection.source, id: selection.id },
      { active: active ? 1 : 0 }
    )

    setSelectedPin(active ? selection : null)
  }

  const highlightPin = (
    feature: MapboxGeoJSONFeature | undefined,
    layerId: 'places' | 'user-generated-locations' | 'social' | undefined
  ) => {
    // we need to blank it out here to ease the render event handler
    // for the scenario where bookmared point has been removed from the data source
    // and so querySourceFeatures will never match it on Viewing a bookmark
    flyToLocationRef.current = null

    setSelectedState(selectedPin, false)

    if (!feature) {
      // reset highlight
      setActive(null)
      togglePlacesLayerOpacity()
      toggleUserGeneratedLocationsLayerOpacity()
      toggleSocialLayerOpacity()
      return
    }
    if (layerId === 'places' || layerId === 'user-generated-locations') {
      // places is a clustered layer
      if (feature.properties) {
        const props: BuildGeoJSONProps = {
          id: feature.properties.id,
          name: feature.properties.name,
          iconType: feature.properties.iconType,
          position: feature.properties.position
        }
        const geoJson = buildGeoJson(props, feature.type)
        setActive(geoJson)
      }

      togglePlacesLayerOpacity(true)
      toggleUserGeneratedLocationsLayerOpacity(true)
    } else if (layerId === 'social') {
      const featureId = feature.id
      // our layer id and source match
      setSelectedState({ id: featureId, source: 'social' }, true)

      toggleSocialLayerOpacity(featureId)
    }
  }

  const onPinSelected = (
    feature: MapboxGeoJSONFeature | undefined,
    layer: 'places' | 'user-generated-locations' | 'social'
  ) => {
    highlightPin(feature, layer)

    if (!feature || !feature.properties) return

    if (layer === 'places' || layer === 'user-generated-locations') {
      const locationId = feature.properties.id
      checkIfSubscribed(locationId, queryDetails)
    } else if (layer === 'social') {
      if (!isProfileCompleted) {
        onTogglePopover('complete-profile')
      } else {
        onTogglePopover(
          'profile',
          {
            userId: feature.properties.userId,
            postId: feature.properties.id
          },
          true
        )
      }
    }
  }

  const displayFeaturesFromCluster = function (
    clusterId: number,
    sourceId: string,
    clusterCoord: Position,
    matchingName: string
  ) {
    if (!mapRef.current) return
     
    const leaves = (mapRef.current.getSource(sourceId) as GeoJSONSource).getClusterLeaves(
      clusterId,
      10,
      0,
      (error, leaves) => {
        //For some reason, leaves are duplicated
        if (error) return

        setLocationPopupData({
          show: true,
          lng: clusterCoord[0],
          lat: clusterCoord[1],
          features: removeDuplicates(leaves),
          matchingName
        })

      }
    )

    console.log(leaves,'levaes')
  }

  // TODO: consider moving to onClick handler inside map
  const onMapLoad = () => {
    setIsMapLoaded(true)
    if (!mapRef.current) return

    mapRef.current.on('click', 'clusters', e => {
      if (!mapRef.current) return

      const feature: Feature<Geometry> | undefined =
        mapRef.current?.queryRenderedFeatures(e.point, {
          layers: ['clusters']
        })[0]

      var layer = getFeatureLayer(feature)

      if (!feature.properties) return
      const clusterId = feature.properties.cluster_id
      const clusterCoord = (feature.geometry as GeoJSON.Point).coordinates
      if (
        feature.properties.clusterName &&
        feature.properties.clusterName !== 'null'
      ) {
        displayFeaturesFromCluster(
          clusterId,
          layer,
          clusterCoord,
          feature.properties.clusterName
        )
        return
      }
      const center = { lng: clusterCoord[0], lat: clusterCoord[1] }

      const map = mapRef.current.getMap()
      const source = map.getSource(layer)

      if (source.type === 'geojson') {
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return

          if (map.getZoom() >= Math.min(map.getMaxZoom(), zoom)) {
            displayFeaturesFromCluster(clusterId, layer, clusterCoord, '')
            return
          }
          mapRef.current?.easeTo({
            center,
            zoom
          })
        })
      }
    })
    mapRef.current.on('click', 'ugl-clusters', e => {
      if (!mapRef.current) return

      const feature: Feature<Geometry> | undefined =
        mapRef.current?.queryRenderedFeatures(e.point, {
          layers: ['ugl-clusters']
        })[0]

      var layer = getFeatureLayer(feature)

      if (!feature.properties) return
      const clusterId = feature.properties.cluster_id
      const clusterCoord = (feature.geometry as GeoJSON.Point).coordinates
      console.log(clusterCoord,'clusterCOORD')

      if (
        feature.properties.clusterName &&
        feature.properties.clusterName !== 'null'
      ) {
        displayFeaturesFromCluster(
          clusterId,
          layer,
          clusterCoord,
          feature.properties.clusterName
        )
        return
      }
      const center = { lng: clusterCoord[0], lat: clusterCoord[1] }

      const map = mapRef.current.getMap()
      const source = map.getSource(layer)

      if (source.type === 'geojson') {
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return

          if (map.getZoom() >= Math.min(map.getMaxZoom(), zoom)) {
            displayFeaturesFromCluster(clusterId, layer, clusterCoord, '')
            return
          }
          mapRef.current?.easeTo({
           center,
            zoom
          })
        })
      }
    })
    mapRef.current.on('click', 'places', function (e) {
      onPinSelected(e.features?.[0], 'places' as const)
    })
    mapRef.current.on('click', 'user-generated-locations', function (e) {
      onPinSelected(e.features?.[0], 'user-generated-locations' as const)
    })
    mapRef.current.on('click', 'social', function (e) {
      onPinSelected(e.features?.[0], 'social' as const)
    })

    const setPointerCursor = function () {
      if (!mapRef.current) return
      mapRef.current.getCanvas().style.cursor = 'pointer'
    }

    const setDefaultCursor = function () {
      if (!mapRef.current) return
      mapRef.current.getCanvas().style.cursor = ''
    }

    // TODO: refactor to use inside map instead
    // or better move to state https://visgl.github.io/react-map-gl/docs/api-reference/map#cursor
    mapRef.current.on('mouseenter', 'clusters', setPointerCursor)
    mapRef.current.on('mouseenter', 'ugl-clusters', setPointerCursor)
    mapRef.current.on('mouseenter', 'places', setPointerCursor)
    mapRef.current.on(
      'mouseenter',
      'user-generated-locations',
      setPointerCursor
    )
    mapRef.current.on('mouseenter', 'social', setPointerCursor)
    mapRef.current.on('mouseenter', 'places-active', setPointerCursor)

    // Change it back to a pointer when it leaves.
    mapRef.current.on('mouseleave', 'clusters', setDefaultCursor)
    mapRef.current.on('mouseleave', 'ugl-clusters', setDefaultCursor)
    mapRef.current.on('mouseleave', 'places', setDefaultCursor)
    mapRef.current.on(
      'mouseleave',
      'user-generated-locations',
      setDefaultCursor
    )
    mapRef.current.on('mouseleave', 'social', setDefaultCursor)
    mapRef.current.on('mouseleave', 'places-active', setDefaultCursor)

    // We initially load just places near to the centre of the map to speed up loading, but now we load all places
    if (mapRef.current) {
      ;(mapRef.current.getSource('places') as GeoJSONSource).setData(
        locations as FeatureCollection
      )
    }
  }

  function getFeatureLayer(feature: Feature<Geometry> | undefined) {
    var featureStringified = JSON.stringify(feature)

    if (
      featureStringified !== null &&
      featureStringified !== undefined &&
      featureStringified !== ''
    ) {
      var featureParsed = JSON.parse(featureStringified)

      if (
        featureParsed !== null &&
        featureParsed !== undefined &&
        featureParsed !== '' &&
        featureParsed.source !== null &&
        featureParsed.source !== undefined &&
        featureParsed.source !== ''
      ) {
        return featureParsed.source
      }
    }

    return 'places'
  }

  /*  function reloadSocialPins() {
    if (!mapRef.current) return
    ;(mapRef.current.getSource('social') as GeoJSONSource).setData(
      socialSource.data
    )
  }*/

  function getMapPinById(pinId: string): IMapPin {
    var mapPin: IMapPin = {
      id: pinId,
      layer: 'social'
    }

    if (!mapRef.current) {
      return mapPin
    }

    let features = mapRef.current.querySourceFeatures(mapPin.layer, {
      filter: ['==', 'id', pinId] // by location id, not feature id
    })

    let feature = features?.[0]

    if (feature) {
      return mapPin
    }

    if (!feature) {
      mapPin.layer = 'user-generated-locations'
      features = mapRef.current.querySourceFeatures(mapPin.layer, {
        filter: ['==', 'id', mapPin.id]
      })

      feature = features?.[0]

      if (feature) {
        return mapPin
      }
    }
    if (!feature) {
      mapPin.layer = 'places'
      features = mapRef.current.querySourceFeatures(mapPin.layer, {
        filter: ['==', 'id', mapPin.id]
      })

      feature = features?.[0]

      if (feature) {
        return mapPin
      }
    }

    return mapPin
  }

  function findFeature(mapPin: IMapPin, callback: FeatureCallback): void {
    if (!mapRef.current) return

    // Find all features in one source layer in a vector source
    let layer = mapPin.layer
    let features = mapRef.current.querySourceFeatures(layer, {
      filter: ['==', 'id', mapPin.id] // by location id, not feature id
    })

    let feature = features?.[0]
    if (!feature) {
      if (layer === 'social') {
        // might be post added to feed after map loaded
        /*reloadSocialPins()*/
        return
      }

      layer = 'user-generated-locations'
      features = mapRef.current.querySourceFeatures(layer, {
        filter: ['==', 'id', mapPin.id] // by location id, not feature id
      })

      feature = features?.[0]
    }

    if (mapPin.layer) {
      callback(feature, mapPin.layer)
    }
  }

  // should be useEffect instead
  const onRender = () => {
    if (!isMapLoaded) return

    if (locationId) {
      findFeature({ id: locationId, layer: 'places' }, onPinSelected)
      setLocationId(null)
    } else {
      if (flyToLocationRef.current?.layer) {
        findFeature(flyToLocationRef.current, highlightPin)
      }
    }
  }

  async function getLocationDetails(
    locationId: string,
    callback: (data: any) => void
  ) {
    try {
      const res = await API.get('/content', {
        params: {
          id: locationId
        }
      })

      callback(res.data)
    } catch (error) {}
  }

  const showDescription = function (data: any, hasAccess: boolean) {
    const propsString = JSON.stringify(data?.message ?? null, fixValues)

    const propsWithoutLinks = JSON.parse(propsString)
    const props = JSON.parse(propsString)

    setDetailsOverlayData({
      props,
      propsWithoutLinks,
      hasAccess
    })

    if (activePopover !== 'place-detailed') {
      onTogglePopover('place-detailed')
    }
  }

  const queryDetails = async function (locationId: string, hasAccess: boolean) {
    await getLocationDetails(locationId, function (data) {
      if (locationToView) {
        showDescription(data, hasAccess)
      }
      // move to relevant tile before we can query feature and select pin if we need to
      if (flyToLocationRef.current?.id === data?.message.id) {
        const geoJson = buildGeoJson(data)
        setActive(geoJson)
        mapRef.current?.flyTo({
          center: [
            data?.message.properties.mapLocation.address.coordinates.lng,
            data?.message.properties.mapLocation.address.coordinates.lng
          ]
        })
        flyToLocationRef.current = null
      }
      showDescription(data, hasAccess)
    })
  }

  const checkIfSubscribed = function (
    locationId: string,
    callback: (id: string, hasAccess: boolean) => void
  ) {
    // User needs to subscribe
    if (
      userSubscriptionStatus &&
      !userSubscriptionStatus?.hasSubscription &&
      userSubscriptionStatus?.trialExpiresIn <= 0
    ) {
      callback(locationId, false)
    } else {
      // User has full access
      callback(locationId, true)
    }
  }

  function focusOnLocation(id: string, layer: string) {
    // we will need to move to relevant tile before we can query feature and select pin
    let mapPin = getMapPinById(id)
    flyToLocationRef.current = {
      id: mapPin.id,
      layer: mapPin.layer as FeatureLayer
    }
    checkIfSubscribed(id, queryDetails)
  }

  return (
    <>
      <Map
        ref={mapRef}
        {...viewState}
        id="map"
        onMove={evt => setViewState(state => ({ ...state, ...evt.viewState }))}
        onMouseMove={evt => {
          setMousePosition(evt.lngLat)
        }}
        onLoad={onMapLoad}
        onRender={onRender}
        onClick={e => {
          if (!mapRef.current) return
          let features = mapRef.current.queryRenderedFeatures(e.point, {
            layers: [
              'clusters',
              /*'ugl-clusters',*/
              'places',
              /*'user-generated-locations',*/
              /*'social',*/
              'places-active'
            ]
          })
          if (features.length > 0) return

          if (!newPostPopupData.show) {
            setNewPostPopupData({
              show: true,
              lng: e.lngLat.lng,
              lat: e.lngLat.lat
            })
          } else {
            setNewPostPopupData({
              show: false,
              lng: 0,
              lat: 0
            })
          }
        }}
        // onMouseEnter
        // TODO: move to css
        style={{
          width: '100vw',
          height: '100vh',
          position: 'unset'
        }}
        mapStyle={mapStyle}
        mapboxAccessToken={MAPBOX_TOKEN}
        dragRotate={false} // disable map rotation using right click + drag
        touchZoomRotate={true} // map rotation using touch rotation gesture
      >
        <CustomSearchBox mapRef={mapRef} />
        {isMapLoaded && chartsAreLoaded && (
          <>
            {chartSetsSources.map((source, index) => (
              <Source {...source} key={index}>
                <Layer {...chartSetsLayers[index]} beforeId="places" />
              </Source>
            ))}

            {chartSetsGeojsonSources.map((source, index) => (
              <Source {...source} key={index}>
                <Layer {...chartSetsGeojsonLayers[index]} beforeId="places" />
              </Source>
            ))}

            {/* {createPortal(
            <>
              {chartSets.map(chartSet => {
                const chartSetName = getChartSetName(chartSet)
                return (
                  <li key={chartSet.name}>
                    <h4>{chartSetName}</h4>
                    {chartSet.parts.map(chartSetPart => {
                      const filename = chartSetPart.name
                        .replace(chartSetName, '')
                        .trim()

                      // dev note chartSetsLayerVisibility is a boolean right now
                      // it will need to be switched to object for this to work

                      const isActive = chartSetsLayerVisibility[chartSetPart.id]

                      return (
                        <input
                          key={filename}
                          type="button"
                          className={`bth ${
                            isActive ? 'btn-secondary' : ''
                          } mt-2 mr-2`}
                          value={filename}
                          onClick={() => {
                            setChartSetsLayerVisibility({
                              ...chartSetsLayerVisibility,
                              [chartSetPart.id]: !isActive
                            })
                          }}
                        />
                      )
                    })}
                  </li>
                )
              })}
              <li>
                <a className="btn-primary" href={subscriptionUrl}>
                  Subscribe
                </a>
              </li>
            </>,
            document.getElementById('my-chart-list') as HTMLElement
          )} */}
          </>
        )}
        {/*        {isMapLoaded && (
          <Source {...socialSource}>
            <Layer {...socialLayerStyle} beforeId="clusters" />
          </Source>
        )}*/}
        <Source {...{ ...placesSource, data: locations }}>
          <Layer {...placesLayerStyle} />
          <Layer {...clustersLayerStyle} />
          <Layer {...clusterCountLayerStyle} />
        </Source>
        {/*      <Source
          {...{
            ...userGeneratedLocationsSource,
            data:
              userGeneratedLocationsSource.data +
              userGeneratedLocationsSourceQuery
          }}
        >
          <Layer {...userGeneratedLocationsLayerStyle} beforeId="clusters" />
          <Layer {...uglClusterLayerStyle} />
          <Layer {...uglClusterCountLayerStyle} />
        </Source>*/}

        {isMapLoaded && (
          <Source {...{ ...placesActiveSource, data: activePlaces }}>
            <Layer {...placesActiveLayerStyle} beforeId="clusters" />
          </Source>
        )}
        {/* top left */}
        <GroupControlContainer selector=".mapboxgl-ctrl-top-left">
          <ProfileControl />
          <FilterControl />
        </GroupControlContainer>
        {/* top right */}
        <GeocoderControl
          mapboxAccessToken={MAPBOX_TOKEN}
          position="top-right"
        />
        <NavigationControl />
        <GroupControlContainer selector=".mapboxgl-ctrl-top-right .mapboxgl-ctrl-group">
          <ZoomDisplayControl
            zoom={viewState.zoom.toFixed(2)}
            showZoomLevel={showZoomLevel}
          />
          <SatelliteViewControl
            isSatelliteView={isSatelliteView}
            toggleSatelliteView={toggleSatelliteView}
          />
          {/*       <ChartsLayersControl
            toggleChartsInfoOverlay={toggleChartsInfoOverlay}
          />
          <CommunityContentControl
            isCommunityContentVisible={isCommunityContentVisible}
            toggleCommunityContent={toggleCommunityContent}
          />*/}
          <BookmarksControl />
        </GroupControlContainer>
        {/* bottom left */}
        <ScaleControl maxWidth={300} unit="nautical" position="bottom-left" />
        <ScaleControl maxWidth={300} unit="metric" position="bottom-left" />
        <GroupControlContainer selector=".mapboxgl-ctrl-bottom-left">
          <InfoControl position={mousePosition} />
          <TrialControl />
        </GroupControlContainer>
        {/* bottom right */}
        <GroupControlContainer selector=".mapboxgl-ctrl-bottom-right">
          <AttributionControl />
        </GroupControlContainer>
        {/*        {newPostPopupData.show && (
          <NewPostPopup
            {...newPostPopupData}
            onClose={() => {
              setNewPostPopupData({ show: false, lng: 0, lat: 0 })
            }}
            zoom={viewState.zoom}
          />
        )}*/}
        {locationPopupData.show && (
          <LocationPopup
            onClose={() =>
              setLocationPopupData({
                show: false,
                lng: 0,
                lat: 0,
                features: [],
                matchingName: ''
              })
            }
            focusOnLocation={focusOnLocation}
            {...locationPopupData}
          />
        )}
      </Map>
      {!isMapLoaded && (
        <div className="loading-spinner-container map-loading-spinner">
          <Spinner animation="border" variant="primary" />
        </div>
      )}
    </>
  )
}

export default MapView
